import { useId, type ReactNode } from 'react';

/**
 * Filtres d'une liste. Une barre au-dessus du tableau, jamais un panneau
 * latéral : la volumétrie (une centaine de mandats) tient en quelques
 * critères, et le tableau reste la pièce maîtresse de l'écran.
 */
export function BarreFiltres({
  children,
  actifs,
  onRetirer,
}: {
  children: ReactNode;
  /** Au moins un critère posé : le bouton de remise à zéro apparaît. */
  actifs?: boolean;
  onRetirer?: () => void;
}) {
  return (
    <div className="filtres">
      {children}
      {actifs && onRetirer && (
        <button
          type="button"
          className="bouton bouton--fantome bouton--compact"
          onClick={onRetirer}
        >
          Retirer les filtres
        </button>
      )}
    </div>
  );
}

/** Liste déroulante de filtrage ; la valeur vide signifie « tous ». */
export function FiltreChoix({
  libelle,
  valeur,
  onChange,
  options,
  tous,
}: {
  libelle: string;
  valeur: string;
  onChange: (valeur: string) => void;
  options: readonly (readonly [string, string])[];
  tous: string;
}) {
  const id = useId();
  return (
    <div className="champ">
      <label className="surtitre" htmlFor={id}>
        {libelle}
      </label>
      <select
        id={id}
        className="champ__select"
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{tous}</option>
        {options.map(([code, texte]) => (
          <option key={code} value={code}>
            {texte}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Recherche plein texte, appliquée à la frappe. */
export function FiltreRecherche({
  libelle = 'Rechercher',
  valeur,
  onChange,
  placeholder,
}: {
  libelle?: string;
  valeur: string;
  onChange: (valeur: string) => void;
  placeholder?: string;
}) {
  const id = useId();
  return (
    <div className="champ champ--large">
      <label className="surtitre" htmlFor={id}>
        {libelle}
      </label>
      <input
        id={id}
        type="search"
        value={valeur}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** Choix exclusif parmi quelques valeurs, toutes visibles d'un coup d'œil. */
export function FiltreSegments({
  valeur,
  onChange,
  options,
  libelle,
}: {
  valeur: string;
  onChange: (valeur: string) => void;
  options: readonly (readonly [string, string])[];
  libelle: string;
}) {
  return (
    <div className="segments" role="group" aria-label={libelle}>
      {options.map(([code, texte]) => (
        <button
          key={code || 'tous'}
          type="button"
          className="segment"
          aria-pressed={valeur === code}
          onClick={() => onChange(code)}
        >
          {texte}
        </button>
      ))}
    </div>
  );
}

/**
 * Pagination d'une liste servie par pages. Dit toujours combien il y a en
 * tout : une liste qui s'arrête sans le dire laisse croire qu'elle est
 * complète.
 */
export function Pagination({
  page,
  taille,
  total,
  onPage,
}: {
  page: number;
  taille: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / taille));
  if (total === 0) return null;
  const debut = (page - 1) * taille + 1;
  const fin = Math.min(total, page * taille);
  return (
    <div className="pagination">
      <span className="pagination__etendue">
        {debut}–{fin} sur {total}
      </span>
      {pages > 1 && (
        <span className="rangee-actions">
          <button
            type="button"
            className="bouton bouton--fantome bouton--compact"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            Précédent
          </button>
          <span className="pagination__etendue">
            page {page} sur {pages}
          </span>
          <button
            type="button"
            className="bouton bouton--fantome bouton--compact"
            disabled={page >= pages}
            onClick={() => onPage(page + 1)}
          >
            Suivant
          </button>
        </span>
      )}
    </div>
  );
}

/** Minuscules sans accents : « Équilibré » se trouve en tapant « equi ». */
function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Vrai si chaque mot saisi figure dans l'un des champs. L'ordre des mots est
 * libre : « alami mdt-2026 » retrouve le mandat de M. Alami.
 */
export function correspond(recherche: string, ...champs: (string | null | undefined)[]): boolean {
  const mots = normaliser(recherche).split(/\s+/).filter(Boolean);
  if (mots.length === 0) return true;
  const botte = normaliser(champs.filter(Boolean).join(' '));
  return mots.every((m) => botte.includes(m));
}

/** Paires (valeur, libellé) distinctes, triées par libellé, pour un filtre. */
export function optionsDistinctes<T>(
  lignes: readonly T[],
  cle: (ligne: T) => [string, string],
): [string, string][] {
  const vues = new Map<string, string>();
  for (const l of lignes) {
    const [code, libelle] = cle(l);
    vues.set(code, libelle);
  }
  return [...vues.entries()].sort((a, b) => a[1].localeCompare(b[1], 'fr'));
}

/** Réponse paginée commune aux routes de liste de l'API. */
export interface PageDe<T> {
  total: number;
  page: number;
  taille: number;
  elements: T[];
}
